import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Chip,
  Alert,
  CircularProgress,
  Divider,
  Stack,
  Grid,
  Paper,
  IconButton,
  Tooltip,
  useTheme,
  Avatar,
} from "@mui/material";
import {
  ArrowBack as ArrowBackIcon,
  AccessTime as AccessTimeIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  WarningAmber as WarningAmberIcon,
  Schedule as ScheduleIcon,
  TrackChanges as RequestIcon,
} from "@mui/icons-material";
import requestsService, {
  RequestDetails,
  RequestType,
  RequestStatus,
  StatusHistoryEntry,
} from "../services/requests-service";

const RequestDetailPage: React.FC = () => {
  const { requestId } = useParams<{ requestId: string }>();
  const navigate = useNavigate();
  const theme = useTheme();

  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [details, setDetails] = useState<RequestDetails | null>(null);

  const loadDetails = useCallback(async () => {
    if (!requestId) {
      setError("Request ID is required");
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const data = await requestsService.getRequestDetails(parseInt(requestId));
      setDetails(data);
    } catch (err: any) {
      setError(err?.message || "Failed to load request details");
    } finally {
      setLoading(false);
    }
  }, [requestId]);

  useEffect(() => {
    loadDetails();
  }, [loadDetails]);

  const handleBackClick = () => {
    navigate("/requests");
  };

  const getRequestTypeLabel = (type: RequestType): string => {
    const labels: Record<RequestType, string> = {
      CONSENT_WITHDRAWAL: "Consent Withdrawal",
      DATA_ACCESS: "Data Access",
      DATA_CORRECTION: "Data Correction",
      DATA_ERASURE: "Data Erasure",
      DATA_PORTABILITY: "Data Portability",
    };
    return labels[type] || type;
  };

  const getRequestStatusLabel = (status: RequestStatus): string => {
    const labels: Record<RequestStatus, string> = {
      PENDING: "Pending",
      IN_PROGRESS: "In Progress",
      COMPLETED: "Completed",
      REJECTED: "Rejected",
      CANCELLED: "Cancelled",
    };
    return labels[status] || status;
  };

  const getStatusColor = (status: RequestStatus): "default" | "primary" | "success" | "warning" | "error" => {
    switch (status) {
      case "PENDING":
        return "default";
      case "IN_PROGRESS":
        return "primary";
      case "COMPLETED":
        return "success";
      case "REJECTED":
      case "CANCELLED":
        return "error";
      default:
        return "default";
    }
  };

  const getPriorityColor = (priority: string): "default" | "warning" | "error" => {
    switch (priority) {
      case "URGENT":
        return "error";
      case "HIGH":
        return "warning";
      default:
        return "default";
    }
  };

  const formatDate = (value: string | null | Date): string => {
    if (!value) return "—";
    const date = typeof value === "string" ? new Date(value) : value;
    if (Number.isNaN(date.getTime())) {
      return String(value);
    }
    return date.toLocaleString(undefined, {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getStatusIcon = (status: RequestStatus) => {
    switch (status) {
      case "COMPLETED":
        return <CheckCircleIcon />;
      case "REJECTED":
      case "CANCELLED":
        return <CancelIcon />;
      case "IN_PROGRESS":
        return <ScheduleIcon />;
      default:
        return <AccessTimeIcon />;
    }
  };

  const renderStatusHistory = () => {
    if (!details?.statusHistory || details.statusHistory.length === 0) {
      return null;
    }

    return (
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <RequestIcon />
            Status History
          </Typography>
          <Divider sx={{ my: 2 }} />
          <Stack spacing={2}>
            {details.statusHistory.map((entry: StatusHistoryEntry, index: number) => (
              <Box key={entry.statusHistoryId}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 1 }}>
                  <Avatar
                    sx={{
                      bgcolor: theme.palette.primary.main,
                      width: 32,
                      height: 32,
                    }}
                  >
                    {index + 1}
                  </Avatar>
                  <Box sx={{ flex: 1 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
                      {entry.oldStatus && (
                        <>
                          <Chip
                            label={getRequestStatusLabel(entry.oldStatus as RequestStatus)}
                            size="small"
                            variant="outlined"
                          />
                          <Typography variant="body2" color="text.secondary">
                            →
                          </Typography>
                        </>
                      )}
                      <Chip
                        label={getRequestStatusLabel(entry.newStatus as RequestStatus)}
                        color={getStatusColor(entry.newStatus as RequestStatus)}
                        size="small"
                      />
                    </Box>
                    <Typography variant="caption" color="text.secondary">
                      {formatDate(entry.changedDate)}
                      {entry.changedByName && ` • by ${entry.changedByName}`}
                    </Typography>
                  </Box>
                </Box>
                {entry.statusChangeReason && (
                  <Box sx={{ ml: 6, mb: 1 }}>
                    <Typography variant="body2" color="text.secondary">
                      <strong>Reason:</strong> {entry.statusChangeReason}
                    </Typography>
                  </Box>
                )}
                {entry.notes && (
                  <Box sx={{ ml: 6 }}>
                    <Typography variant="body2" color="text.secondary">
                      <strong>Notes:</strong> {entry.notes}
                    </Typography>
                  </Box>
                )}
                {index < details.statusHistory!.length - 1 && <Divider sx={{ mt: 2 }} />}
              </Box>
            ))}
          </Stack>
        </CardContent>
      </Card>
    );
  };

  if (loading) {
    return (
      <Box sx={{ p: 3, display: "flex", justifyContent: "center", alignItems: "center", minHeight: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Button startIcon={<ArrowBackIcon />} onClick={handleBackClick}>
          Back to Requests
        </Button>
      </Box>
    );
  }

  if (!details) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="warning">Request not found</Alert>
        <Button startIcon={<ArrowBackIcon />} onClick={handleBackClick} sx={{ mt: 2 }}>
          Back to Requests
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}>
        <IconButton onClick={handleBackClick} aria-label="Back to requests">
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h4" sx={{ fontWeight: 600 }}>
          Request Details
        </Typography>
      </Box>

      <Grid container spacing={3}>
        {/* Main Details Card */}
        <Grid item xs={12} md={8}>
          <Card>
            <CardContent>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 3 }}>
                <Box>
                  <Typography variant="h5" sx={{ fontWeight: 600, mb: 1 }}>
                    {getRequestTypeLabel(details.requestType)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Request ID: #{details.requestId}
                  </Typography>
                </Box>
                <Stack direction="row" spacing={1}>
                  <Chip
                    label={getRequestStatusLabel(details.requestStatus)}
                    color={getStatusColor(details.requestStatus)}
                    icon={getStatusIcon(details.requestStatus)}
                  />
                  <Chip
                    label={details.priority}
                    color={getPriorityColor(details.priority)}
                    variant="outlined"
                  />
                </Stack>
              </Box>

              <Divider sx={{ my: 3 }} />

              <Stack spacing={3}>
                <Box>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Description
                  </Typography>
                  <Typography variant="body1">{details.description || "No description provided"}</Typography>
                </Box>

                {details.reason && (
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Reason
                    </Typography>
                    <Typography variant="body1">{details.reason}</Typography>
                  </Box>
                )}

                {details.purposeName && (
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Related Purpose
                    </Typography>
                    <Typography variant="body1">{details.purposeName}</Typography>
                    {details.purposeKey && (
                      <Typography variant="caption" color="text.secondary">
                        Key: {details.purposeKey}
                      </Typography>
                    )}
                  </Box>
                )}

                {details.requestedData?.dataElementIds && (
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Related Data Elements
                    </Typography>
                    <Stack direction="row" spacing={1} flexWrap="wrap">
                      {details.requestedData.dataElementIds.map((id: number) => (
                        <Chip
                          key={id}
                          label={`Data Element #${id}`}
                          size="small"
                          onClick={() => navigate(`/data/${id}`)}
                          sx={{ cursor: "pointer" }}
                        />
                      ))}
                    </Stack>
                  </Box>
                )}
              </Stack>
            </CardContent>
          </Card>

          {/* Status History */}
          {renderStatusHistory()}
        </Grid>

        {/* Sidebar with Dates and Info */}
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Timeline
              </Typography>
              <Divider sx={{ my: 2 }} />
              <Stack spacing={2}>
                <Box>
                  <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                    Created
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {formatDate(details.createdDate)}
                  </Typography>
                </Box>

                {details.estimatedCompletionDate && (
                  <Box>
                    <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                      Estimated Completion
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 500, color: theme.palette.warning.main }}>
                      {formatDate(details.estimatedCompletionDate)}
                    </Typography>
                  </Box>
                )}

                {details.actualCompletionDate && (
                  <Box>
                    <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                      Completed
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 500, color: theme.palette.success.main }}>
                      {formatDate(details.actualCompletionDate)}
                    </Typography>
                  </Box>
                )}

                {details.modifiedDate && (
                  <Box>
                    <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                      Last Modified
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {formatDate(details.modifiedDate)}
                    </Typography>
                  </Box>
                )}
              </Stack>
            </CardContent>
          </Card>

          {/* Quick Actions */}
          <Card sx={{ mt: 2 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Quick Actions
              </Typography>
              <Divider sx={{ my: 2 }} />
              <Stack spacing={1}>
                <Button
                  variant="outlined"
                  fullWidth
                  startIcon={<ArrowBackIcon />}
                  onClick={handleBackClick}
                >
                  Back to Requests
                </Button>
                {details.requestedData?.dataElementIds && details.requestedData.dataElementIds.length > 0 && (
                  <Button
                    variant="outlined"
                    fullWidth
                    onClick={() => navigate(`/data/${details.requestedData.dataElementIds[0]}`)}
                  >
                    View Related Data Element
                  </Button>
                )}
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default RequestDetailPage;
